import React from 'react';
import ApolloClient from 'apollo-boost';
import { ApolloProvider } from '@apollo/react-hooks';
import { format, startOfToday, endOfYear, addDays } from 'date-fns';
import styled from 'styled-components';
import { Row, Col, Card, Skeleton } from 'antd';
import { SecondarySectionTitle } from '../components/styles';
import LazyLoad from 'react-lazyload';

import Layout from '../components/layout';
import { SettingProvider } from '../lib/context/settings';
import { AdsProvider } from '../lib/context/ads';
import { useSimpleFetch } from '../utils/hooks';
import { device } from '../lib/theme';

const { Meta } = Card;

const emptyEvents = new Array(4).fill('');

const dateFormat = 'yyyy-MM-dd HH:mm:dd';
const today = format(startOfToday(), dateFormat);
const lastDayOfYear = format(
  endOfYear(addDays(startOfToday(), 15)),
  dateFormat
);
const countPerPage = 1000;
const uri = (page = 1, startDate = today, endDate = lastDayOfYear) =>
  `https://${process.env.GATSBY_WORDPRESS_URL_PATH}/wp-json/tribe/events/v1/events?page=${page}&per_page=${countPerPage}&status=publish&start_date=${startDate}&end_date=${endDate}`;

const client = new ApolloClient<any>({
  uri: `https://${process.env.GATSBY_WORDPRESS_URL_PATH}/graphql`,
  fetchOptions: {
    credentials: 'include',
  },
});

type EventData = {
  id: number;
  slug: string;
  title: string;
  description: string;
  excerpt: string;
  image: {
    url: string;
    width: number;
    height: number;
  };
  all_day: boolean;
  utc_start_date_details: {
    year: string;
    month: string;
    day: string;
    hour: string;
    minutes: string;
    seconds: string;
  };
  utc_end_date_details: {
    year: string;
    month: string;
    day: string;
    hour: string;
    minutes: string;
    seconds: string;
  };
  cost: string; // 'Free'
  cost_details: {
    currency_symbol: string;
    currency_position: 'prefix';
    values: Array<string>;
  };
  website: string;
  hide_from_listings: boolean;
  sticky: boolean;
  featured: boolean;
  categories: [];
  tags: [];
  venue: {
    url: string;
    venue: string;
    state: string;
    show_map: boolean;
    show_map_link: boolean;
  };
  organizer: [
    {
      url: string;
      organizer: string;
    }
  ];
};

const SectionContainer = styled(Row)`
  padding: 8px 0;

  .ant-card-body {
    min-height: ${({ longForm }: { longForm: boolean }) =>
      longForm ? 300 : 160}px;
    position: relative;
  }
  .ant-card-meta-title {
    margin-bottom: 0 !important;
    text-overflow: ellipsis;
  }
  .ant-card-meta-description {
    font-family: 'CG-regular', sans-serif;
    font-size: 16px;
    line-height: 20px;
    color: #2c2c2c;
    min-height: 140px;
  }

  .ant-card .event-action {
    background: transparent;
    border: none;
    color: #cd2127;
    font-family: 'CG-bold', sans-serif;
    font-weight: 400;
    font-size: 16px;
    padding: 0;
    display: flex;
    align-items: center;
    position: absolute;
    bottom: 24px;
  }

  .ant-card .event-action > img {
    margin-left: 8px;
  }

  .ant-carousel {
    width: 100%;
  }

  @media ${device.tablet} {
    padding: 64px 0;
  }
`;

const positionAbsolute: React.CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  bottom: 0,
  right: 0,
};

const Events: React.FC<any> = () => {
  const { loading, error, data } = useSimpleFetch<{
    events: Array<EventData>;
    total_pages: number;
  }>(uri());

  const displayRow = data
    ? data.events.map((event) => (
        <Col
          xs={{ span: 24 }}
          sm={{ span: 12 }}
          lg={{ span: 6 }}
          key={event.id}
        >
          <LazyLoad height={350} once>
            <Card
              hoverable
              cover={
                <div
                  style={{
                    backgroundImage: `url(${event.image.url})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    paddingTop: '45%',
                  }}
                />
              }
            >
              <Meta
                title={
                  event.title ? (
                    <SecondarySectionTitle
                      marginBottom={16}
                      title={event.title}
                    >
                      {event.title}
                    </SecondarySectionTitle>
                  ) : null
                }
                description={
                  <div
                    dangerouslySetInnerHTML={{ __html: event.description }}
                  />
                }
              />
              <a className="event-action" href={`/event/${event.slug}`}>
                Learn more
                <img src="/img/icon/right-arrow.png" />
              </a>
            </Card>
          </LazyLoad>
        </Col>
      ))
    : null;

  return (
    <div style={{ paddingTop: 50 }}>
      {loading ? (
        <Row>
          <Col xs={{ span: 22, offset: 1 }} xl={{ span: 20, offset: 2 }}>
            <SectionContainer
              gutter={[16, 16]}
              style={{ margin: 0 }}
              longForm={true}
            >
              {emptyEvents.map((_, idx) => (
                <Col xs={{ span: 12 }} lg={{ span: 6 }} key={idx}>
                  <Card
                    hoverable
                    cover={
                      <div
                        className="ant-skeleton ant-skeleton-active"
                        style={{
                          paddingTop: '45%',
                          position: 'relative',
                        }}
                      >
                        <div
                          className="ant-skeleton-content"
                          style={positionAbsolute}
                        >
                          <ul
                            className="ant-skeleton-paragraph"
                            style={{ ...positionAbsolute, marginBottom: 0 }}
                          >
                            <li
                              style={{ ...positionAbsolute, height: 'auto' }}
                            ></li>
                          </ul>
                        </div>
                      </div>
                    }
                  >
                    <Skeleton active />
                    <Skeleton.Button active />
                  </Card>
                </Col>
              ))}
            </SectionContainer>
          </Col>
        </Row>
      ) : null}
      {error ? error.message : null}
      {!loading && !error ? (
        <div>
          <h1 style={{ marginTop: 0, marginBottom: -30, textAlign: 'center' }}>
            Events
          </h1>
          <Row>
            <Col xs={{ span: 22, offset: 1 }} xl={{ span: 20, offset: 2 }}>
              <SectionContainer
                gutter={[16, 16]}
                style={{ margin: 0 }}
                longForm
              >
                {displayRow}
              </SectionContainer>
            </Col>
          </Row>
        </div>
      ) : null}
    </div>
  );
};

const EventsPage: React.FC<any> = () => {
  return (
    <ApolloProvider client={client as any}>
      <SettingProvider>
        <AdsProvider>
          <Layout key={`${typeof window}`}>
            <Events />
          </Layout>
        </AdsProvider>
      </SettingProvider>
    </ApolloProvider>
  );
};

export default EventsPage;
